import React, { useContext } from 'react';
import { bool, number, object, oneOf, string, oneOfType } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import PaymentOptionContext from 'components/PaymentOptionList/context/PaymentOptionContext';

import history from 'history.js';

import { LIVE_TOPIC } from 'constants/liveEventSections';
import { BASE, INTRODUCTORY, PRESALE } from 'constants/priceTypes';
import { BUNDLE, SINGLE, PREPACK } from 'constants/checkoutCollectionTypes';
import { COURSE, REGULAR_CLASS } from 'constants/contentTypes';
import {
  SINGLE_PURCHASE_TEMPLATE,
  PREPACK_PURCHASE_TEMPLATE,
  CONTENT_BUNDLE_TEMPLATE,
} from 'constants/routingPath';
import { PURCHASE_PAYMENT_OPTION_ITEM_CLICK } from 'constants/gtmEvent';

import useGetOfficialSiteURL from 'hooks/useGetOfficialSiteURL';

import { push } from 'reduxModules/navigation/action';
import { sendGTMEvent } from 'reduxModules/googleTagManager/action';

import Button from 'components/Button';

export default function PaymentOptionButton({
  type,
  discount,
  checkoutId,
  isHighlighted,
  contentType,
  contentId,
  buttonProps,
}) {
  const { t } = useTranslation(['purchase']);
  const { location } = history;

  const dispatch = useDispatch();

  const { onClickPaymentOption } = useContext(PaymentOptionContext);

  const getOfficialSiteURL = useGetOfficialSiteURL();

  const buttonType = discount && discount.type !== BASE ? discount.type : type;

  const buttonTextMap = {
    [INTRODUCTORY]: t('purchase.content.cta.firstMonthDeal', {
      percentage: discount?.percentage,
      percentageZH: discount?.rate,
    }),
    [PRESALE]: t('purchase.content.cta.preOrder'),
    [SINGLE]: t('purchase.content.cta.getItNow'),
    [BUNDLE]: t('purchase.content.cta.select'),
    [PREPACK]: t('purchase.content.cta.getItNow'),
  };

  const handleGoPurchase = () =>
    dispatch(
      push(
        getOfficialSiteURL(
          `${SINGLE_PURCHASE_TEMPLATE}#from=${contentType}:${contentId}`,
          {
            id: checkoutId,
          },
        ),
      ),
    );

  const handleGoBundlePage = () =>
    dispatch(
      push({
        pathname: getOfficialSiteURL(CONTENT_BUNDLE_TEMPLATE, {
          id: checkoutId,
        }),
        hash: `${contentType}=${contentId}`,
        state: {
          from: location.pathname,
        },
      }),
    );

  const handleGoPurchasePrepack = () =>
    dispatch(
      push(
        getOfficialSiteURL(PREPACK_PURCHASE_TEMPLATE, {
          id: checkoutId,
        }),
      ),
    );

  const handleClickPurchase = () => {
    if (typeof onClickPaymentOption === 'function') {
      onClickPaymentOption();
    }

    dispatch(
      sendGTMEvent({
        eventName: PURCHASE_PAYMENT_OPTION_ITEM_CLICK,
        data: {
          type: contentType,
          contentId,
          bundleType: type,
          checkoutCollectionId: checkoutId,
          section: LIVE_TOPIC,
        },
      }),
    );
    switch (type) {
      case BUNDLE:
        handleGoBundlePage();
        return;

      case PREPACK:
        handleGoPurchasePrepack();
        return;

      default:
        handleGoPurchase();
    }
  };

  return (
    <Button
      text={buttonTextMap[buttonType]}
      block
      outline={!isHighlighted}
      onClick={handleClickPurchase}
      {...buttonProps}
    />
  );
}

PaymentOptionButton.propTypes = {
  checkoutId: number.isRequired,
  type: oneOf([SINGLE, BUNDLE, PREPACK]).isRequired,
  discount: object,
  isHighlighted: bool,
  contentType: oneOf([COURSE, REGULAR_CLASS]).isRequired,
  contentId: oneOfType([string, number]).isRequired,
  buttonProps: object,
};

PaymentOptionButton.defaultProps = {
  discount: undefined,
  isHighlighted: false,
  buttonProps: {},
};
